import type { TimedEventFire } from "@/player/lib/scheduling/types";

export interface TimedEventSchedulerCallbacks {
  onFire: (event: TimedEventFire) => void;
  isBroadcastActive: () => boolean;
  /** True when an ad insertion is still on air (skip overlapping interval ticks). */
  isAdStillPlaying: () => boolean;
  queueAfterBroadcast: (event: TimedEventFire) => void;
  /**
   * Clock used for delays AND the fire-time staleness re-check. Callers pass
   * the server-corrected clock: fireAt instants are stamped by the SERVER, so
   * comparing them against the raw device clock silently discarded every fire
   * within the skew on fast devices, and fired minutes late on slow ones.
   */
  nowMs?: () => number;
  /** A timer fired far past its instant (suspend/resume) and was skipped. */
  onStaleFire?: (event: TimedEventFire, lateByMs: number) => void;
}

/**
 * How late a timer may fire and still count as on time. Browsers clamp and
 * defer timers in background tabs, and a suspended machine delivers every
 * armed timer in one burst on wake — firing an announcement scheduled hours
 * ago into the middle of the current program is worse than skipping it.
 */
export const STALE_FIRE_TOLERANCE_MS = 90_000;

/**
 * Arms local timers from a sync payload's timedEvents[]. Applies late-start
 * skip: events whose fireAt is already in the past are never fired. Designed
 * to be RE-armed on every payload on one long-lived instance — the
 * deferred-during-broadcast queue lives on the instance and must survive
 * payload refreshes (broadcasts routinely outlast the poll interval).
 */
export class TimedEventScheduler {
  private timers: number[] = [];
  private queuedWhileBroadcast: TimedEventFire[] = [];

  constructor(private readonly callbacks: TimedEventSchedulerCallbacks) {}

  private nowMs(): number {
    return this.callbacks.nowMs?.() ?? Date.now();
  }

  arm(
    events: TimedEventFire[],
    now = this.nowMs(),
    onSkipped?: (events: TimedEventFire[]) => void,
  ): void {
    this.disarm();
    const skipped: TimedEventFire[] = [];
    const future = events
      .filter((e) => {
        const late = new Date(e.fireAt).getTime() <= now;
        if (late) skipped.push(e);
        return !late;
      })
      .sort(
        (a, b) =>
          new Date(a.fireAt).getTime() - new Date(b.fireAt).getTime(),
      );

    if (skipped.length > 0) onSkipped?.(skipped);

    for (const event of future) {
      const fireAtMs = new Date(event.fireAt).getTime();
      const delay = Math.max(0, fireAtMs - now);
      const timer = window.setTimeout(() => {
        // Re-check at fire time: a timer armed before a sleep/suspend is
        // delivered on wake regardless of how much wall time passed.
        const lateBy = this.nowMs() - fireAtMs;
        if (lateBy > STALE_FIRE_TOLERANCE_MS) {
          this.callbacks.onStaleFire?.(event, lateBy);
          return;
        }
        this.handleFire(event);
      }, delay);
      this.timers.push(timer);
    }
  }

  /** Replay events that were deferred while a live broadcast was active. */
  flushBroadcastQueue(): void {
    const pending = [...this.queuedWhileBroadcast];
    this.queuedWhileBroadcast = [];
    for (const event of pending) {
      this.handleFire(event);
    }
  }

  disarm(): void {
    for (const id of this.timers) window.clearTimeout(id);
    this.timers = [];
  }

  private handleFire(event: TimedEventFire): void {
    if (this.callbacks.isBroadcastActive()) {
      this.callbacks.queueAfterBroadcast(event);
      return;
    }
    if (event.channelType === "ads" && this.callbacks.isAdStillPlaying()) {
      return;
    }
    this.callbacks.onFire(event);
  }

  queueForBroadcast(event: TimedEventFire): void {
    this.queuedWhileBroadcast.push(event);
  }
}
